import React, { useEffect, useState } from 'react';
import Modal from '../components/Modal';
import LoadingSpinner from '../components/LoadingSpinner';
import { apiService } from '../services/apiService';
import { useAuth } from '../services/authContext';

const CATEGORIES = ['academic', 'exam', 'cultural', 'sports', 'holiday', 'other'];
const emptyForm  = { title: '', description: '', startDate: '', endDate: '', location: '', category: 'academic', color: '#5D4037' };

const Calendar = () => {
  const { user }                        = useAuth();
  const isAdmin                         = user?.role === 'admin';
  const [events, setEvents]             = useState([]);
  const [loading, setLoading]           = useState(true);
  const [saving, setSaving]             = useState(false);
  const [error, setError]               = useState('');
  const [currentDate, setCurrentDate]   = useState(new Date());
  const [view, setView]                 = useState('month');
  const [isModalOpen, setIsModalOpen]   = useState(false);
  const [current, setCurrent]           = useState(null);
  const [form, setForm]                 = useState(emptyForm);

  const load = async () => {
    try {
      setLoading(true);
      const data = await apiService.getAll('calendar');
      setEvents(data || []);
    } catch { setError('Failed to load events.'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openAdd = () => {
    const today = new Date().toISOString().split('T')[0];
    setCurrent(null);
    setForm({ ...emptyForm, startDate: today, endDate: today });
    setIsModalOpen(true);
  };

  const openEdit = ev => { setCurrent(ev); setForm({ ...ev }); setIsModalOpen(true); };

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(p => ({ ...p, [name]: value }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    try {
      setSaving(true); setError('');
      if (current) {
        await apiService.update('calendar', current.id, form);
      } else {
        await apiService.create('calendar', form);
      }
      setIsModalOpen(false);
      await load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save event.');
    } finally { setSaving(false); }
  };

  const handleDelete = async id => {
    if (!window.confirm('Delete this event?')) return;
    try {
      await apiService.delete('calendar', id);
      await load();
    } catch { setError('Failed to delete event.'); }
  };

  const year  = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const getDays = () => {
    const days     = [];
    const firstDay = new Date(year, month, 1).getDay();
    const prevDays = new Date(year, month, 0).getDate();
    for (let i = firstDay - 1; i >= 0; i--)
      days.push({ date: new Date(year, month - 1, prevDays - i), current: false });
    const total = new Date(year, month + 1, 0).getDate();
    for (let i = 1; i <= total; i++)
      days.push({ date: new Date(year, month, i), current: true });
    const last = new Date(year, month, total).getDay();
    for (let i = 1; i < 7 - last; i++)
      days.push({ date: new Date(year, month + 1, i), current: false });
    return days;
  };

  const eventsForDay = date =>
    events.filter(e => {
      const s = new Date(e.startDate); s.setHours(0, 0, 0, 0);
      const en = new Date(e.endDate);  en.setHours(23, 59, 59, 999);
      return date >= s && date <= en;
    });

  const navigate = dir => {
    const d = new Date(currentDate);
    d.setMonth(d.getMonth() + dir);
    setCurrentDate(d);
  };

  const title = currentDate.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });

  if (loading) return <LoadingSpinner message="Loading calendar..." />;

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Academic Calendar</h1>
        {isAdmin && <button className="btn btn-primary" onClick={openAdd}>+ Add Event</button>}
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="calendar-controls">
        <div className="calendar-navigation">
          <button onClick={() => navigate(-1)}>‹ Prev</button>
          <button onClick={() => setCurrentDate(new Date())}>Today</button>
          <button onClick={() => navigate(1)}>Next ›</button>
        </div>
        <div className="calendar-title"><h2>{title}</h2></div>
        <div className="view-controls">
          <button className={view === 'month' ? 'active' : ''} onClick={() => setView('month')}>Month</button>
          <button className={view === 'list'  ? 'active' : ''} onClick={() => setView('list')}>List</button>
        </div>
      </div>

      {view === 'month' ? (
        <div className="calendar-month">
          <div className="calendar-weekdays">
            {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d => <div key={d}>{d}</div>)}
          </div>
          <div className="calendar-days">
            {getDays().map((day, i) => {
              const dayEvs  = eventsForDay(day.date);
              const isToday = day.date.toDateString() === new Date().toDateString();
              return (
                <div key={i} className={`calendar-day ${!day.current ? 'other-month' : ''} ${isToday ? 'today' : ''}`}>
                  <div className="day-header">{day.date.getDate()}</div>
                  <div className="day-events">
                    {dayEvs.slice(0, 3).map(ev => (
                      <div key={ev.id} className="event-pill" style={{ backgroundColor: ev.color }}
                        onClick={e => { e.stopPropagation(); isAdmin && openEdit(ev); }}>
                        {ev.title}
                      </div>
                    ))}
                    {dayEvs.length > 3 && <div className="more-events">+{dayEvs.length - 3} more</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div>
          {events.length === 0 ? (
            <div className="no-data-message">No events scheduled.</div>
          ) : [...events].sort((a, b) => new Date(a.startDate) - new Date(b.startDate)).map(ev => (
            <div key={ev.id} className="day-event-item" style={{ borderLeft: `4px solid ${ev.color}` }}>
              <div className="event-time">
                <span style={{ background: ev.color, color: '#fff', padding: '0.2rem 0.5rem', borderRadius: 4, fontSize: '0.8rem' }}>{ev.category}</span>
                <div style={{ marginTop: '0.25rem', fontSize: '0.85rem', color: 'var(--text-light)' }}>
                  {new Date(ev.startDate).toLocaleDateString()}
                  {ev.endDate !== ev.startDate && ` – ${new Date(ev.endDate).toLocaleDateString()}`}
                </div>
              </div>
              <div className="event-content">
                <div className="event-title" style={{ color: ev.color }}>{ev.title}</div>
                {ev.location    && <div className="event-location">📍 {ev.location}</div>}
                {ev.description && <div className="event-description">{ev.description}</div>}
              </div>
              {isAdmin && (
                <div className="event-actions">
                  <button className="btn-icon" onClick={() => openEdit(ev)}>✏️</button>
                  <button className="btn-icon" onClick={() => handleDelete(ev.id)}>🗑️</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {isAdmin && (
        <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={current ? 'Edit Event' : 'Add Event'}>
          <form onSubmit={handleSubmit} className="form">
            <div className="form-group">
              <label>Title</label>
              <input name="title" value={form.title} onChange={handleChange} required />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Start Date</label>
                <input type="date" name="startDate" value={form.startDate} onChange={handleChange} required />
              </div>
              <div className="form-group">
                <label>End Date</label>
                <input type="date" name="endDate" value={form.endDate} onChange={handleChange} required />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Category</label>
                <select name="category" value={form.category} onChange={handleChange}>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Colour</label>
                <input type="color" name="color" value={form.color} onChange={handleChange} />
              </div>
            </div>
            <div className="form-group">
              <label>Location</label>
              <input name="location" value={form.location} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea name="description" rows={3} value={form.description} onChange={handleChange} />
            </div>
            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setIsModalOpen(false)}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Saving...' : current ? 'Update' : 'Add'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
};

export default Calendar;
